import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Calendar as CalendarIcon,
  CheckCircle,
  ChevronDown,
  ClipboardCheck,
  Crosshair,
  Eye,
  GraduationCap,
  HeartHandshake,
  Home,
  Menu,
  MapPin,
  Plane,
  Minus,
  Search,
  Shield,
  ShieldCheck,
  Smile,
  Plus,
  X,
  Zap,
} from "lucide-react";

const C = {
  red: "#e10600",
  redLight: "#ff1a14",
  black: "#000000",
  zinc950: "#09090b",
  zinc900: "#18181b",
  zinc800: "#27272a",
  zinc700: "#3f3f46",
  zinc500: "#71717a",
  zinc400: "#a1a1aa",
  zinc300: "#d4d4d8",
  white: "#ffffff",
};

const EASE = [0.22, 1, 0.36, 1];

const CATEGORY_DATA = {
  schools: {
    id: "schools",
    title: "Schools & Educational Institutions",
    icon: GraduationCap,
    subheadline: "Ensuring safe learning environments for students, staff, and faculty through proactive training.",
    who: ["Teachers and educators (K-12 and higher ed)", "School administrators and leadership", "Campus security and resource officers", "Support staff and facilities teams"],
    problemHeadline: "The Need for Preparedness",
    problem: "Educational institutions must maintain a welcoming atmosphere while simultaneously preparing for unpredictable safety challenges. Staff must be equipped to act decisively in the critical minutes before help arrives.",
    solution: ["Threat recognition and assessment", "Site-specific situational awareness", "Emergency response and lockdown protocols", "De-escalation for disruptive behavior"],
  },
  worship: {
    id: "worship",
    title: "Places of Worship",
    icon: HeartHandshake,
    subheadline: "Comprehensive safety planning that respects the sanctity of your congregation.",
    who: ["Clergy and faith leaders", "Volunteer safety teams and ushers", "Congregation members", "Ministry event coordinators"],
    problemHeadline: "Securing the Sanctuary",
    problem: "Religious facilities are often viewed as open targets. The challenge is implementing effective security measures without compromising the spiritual mission of the institution.",
    solution: ["Greeter and usher safety training", "Pre-incident indicator recognition", "Verbal de-escalation for crisis events", "Safety team development and strategy"],
  },
  healthcare: {
    id: "healthcare",
    title: "Healthcare Facilities",
    icon: Activity,
    subheadline: "Protecting medical professionals in high-stress clinical environments.",
    who: ["Doctors and nursing staff", "Facility administrators", "Front desk personnel", "Behavioral health teams"],
    problemHeadline: "Clinical Safety Management",
    problem: "Healthcare settings are high-stress by nature. Data shows that workplace violence risks are significantly higher in clinical environments than in most other industries.",
    solution: ["Advanced verbal de-escalation techniques", "Self-protection and positioning", "Recognizing behavioral baseline shifts", "Defensive strategies for clinical staff"],
  },
  business: {
    id: "business",
    title: "Businesses & Corporate Teams",
    icon: Briefcase,
    subheadline: "Fostering a culture of safety and confidence within the professional workplace.",
    who: ["Executive leadership", "HR professionals", "Customer-facing teams", "Corporate security personnel"],
    problemHeadline: "Corporate Responsibility",
    problem: "From office security to managing internal disputes, businesses need comprehensive safety plans that exceed standard compliance requirements.",
    solution: ["Workplace violence prevention", "Office security and visitor protocols", "Emergency response planning", "Executive safety and travel awareness"],
  },
  realestate: {
    id: "realestate",
    title: "Real Estate Professionals",
    icon: Home,
    subheadline: "Personal safety strategies for agents working in solitary or unfamiliar settings.",
    who: ["Residential and commercial agents", "Leasing professionals", "Independent solo practitioners", "Property managers"],
    problemHeadline: "Solo Professional Safety",
    problem: "Real estate professionals frequently meet unfamiliar individuals in vacant properties. These transitional movements require specialized awareness to mitigate risk.",
    solution: ["Client screening and safety checklists", "High-risk environment awareness", "Strategic positioning during showings", "Escape strategies for solo professionals"],
  },
  government: {
    id: "government",
    title: "Government Agencies",
    icon: Shield,
    subheadline: "Sophisticated threat mitigation for sensitive government and public-service environments.",
    who: ["Government agency personnel", "Protective services", "Public-facing staff", "Restricted facility contractors"],
    problemHeadline: "Government Agency Risk Mitigation",
    problem: "Personnel in sensitive roles face unique safety challenges. Generic security training is often insufficient for the specific threats encountered by government staff.",
    solution: ["Surveillance detection and mitigation", "Customized insider threat recognition", "Protective coordination protocols", "Dynamic incident response strategies"],
  },
  transportation: {
    id: "transportation",
    title: "Transportation & Travel",
    icon: Plane,
    subheadline: "Tactical preparedness for high-risk, confined-space environments.",
    who: ["Public transit operators", "Aviation and airport personnel", "Maritime and cruise staff", "Logistics and travel coordinators"],
    problemHeadline: "Fast-Moving Risk Environments",
    problem: "Preparing personnel operating in public transportation systems to recognize threats, manage confined-space incidents, and respond effectively in fast-moving, high-risk situations.",
    solution: ["Confined-space threat management", "In-transit situational awareness", "Rapid response in enclosed environments", "Incident communication protocols"],
  },
  events: {
    id: "events",
    title: "Events & Public Spaces",
    icon: MapPin,
    subheadline: "Scalable safety and preparedness solutions for dynamic, high-traffic environments.",
    who: ["Concert and arena staff", "Sporting venue personnel", "Theaters and shopping centers", "Outdoor event coordinators"],
    problemHeadline: "Crowd & Venue Safety",
    problem: "Large crowds and dynamic conditions require heightened awareness and coordinated response strategies for shopping centers, arenas, and outdoor venues.",
    solution: ["Anomaly detection in large crowds", "Heightened situational awareness", "Coordinated response strategies", "Staff preparedness for high-traffic zones"],
  },
};

const EXPERTISE_SECTIONS = [
  { title: "Situational Awareness", icon: Eye, description: "Developing the mindset to observe, orient, and make critical decisions before an incident escalates.", points: ["Pre-incident indicators", "Environmental scanning", "Baseline recognition", "Avoidance strategies"] },
  { title: "Verbal De-escalation", icon: Zap, description: "Mastering the art of communication to diffuse volatile situations and manage aggressive behavior.", points: ["Conflict resolution", "Emotional intelligence", "Verbal tactical positioning", "Diffusing aggression"] },
  { title: "Emergency Response", icon: AlertTriangle, description: "Structured, reality-based protocols for active threat events and critical emergencies.", points: ["Active shooter response", "Facility lockdown drills", "Team coordination", "Panic management"] },
  { title: "Security Advising", icon: ShieldCheck, description: "Professional consulting to identify vulnerabilities and modernize organizational safety infrastructure.", points: ["Vulnerability assessments", "Policy development", "Site security audits", "Strategy planning"] },
];

const CAPABILITIES_DATA = [
  { title: "Security & Preparedness Consulting", icon: ClipboardCheck, benefit: "Strengthens your organization's security foundation, reduces vulnerabilities, and creates a proactive safety mindset.", points: ["On-site evaluation of current security manuals, policies, and procedures", "Recommendations for improvement and alignment with best practices", "Guidance on building a safer, more aware organizational culture"] },
  { title: "Threat Recognition & Prevention", icon: Search, benefit: "Enables your team to detect and disrupt threats before they become critical incidents.", points: ["Identification of behavioral indicators of potential violence", "Recognition of physical and emotional warning signs", "Early-intervention strategies to reduce risks before escalation", "Training to identify concealed weapons and suspicious behavior"] },
  { title: "Hands-On Response Training", icon: Shield, benefit: "Develops confidence, improves reaction time, and builds the ability to take decisive action in high-pressure situations.", description: "Practical, controlled, scenario-based exercises designed to build confidence and capability under stress.", points: ["Lessons learned from real-world active threat incidents", "Foundational personal safety and self-defense skills", "Defensive techniques including strikes, escapes, and control methods", "Safe and practical use of improvised defensive tools", "First-responder coordination and emergency response awareness", "Defense concepts against common weapon threats"] },
  { title: "Realistic Stress & Awareness Drills", icon: Activity, benefit: "Ensures operational readiness by testing protocols in realistic, high-pressure simulations.", description: "Participants engage in structured, high-pressure simulations that mirror real-world conditions.", points: ["Decision-making under stress", "Team communication and coordination", "Tactical positioning during active drills", "Post-incident performance analysis"] },
  { title: "Wellness & Team Building Programs", icon: Smile, benefit: "Improves morale, supports employee wellbeing, strengthens teamwork, reduces workplace anxiety, and builds a more resilient organization.", description: "Training that serves as a powerful wellness and team-building experience, strengthening individual confidence and organizational cohesion.", points: ["Team-based exercises that build trust, communication, and collaboration", "Stress management techniques and performance under pressure", "Confidence-building through practical skill development", "Partner and group drills that reinforce coordination", "Situational awareness training enhancing workplace mindfulness", "Custom sessions for employee wellness initiatives"] },
];

const DIFFERENTIATORS = [
  { title: "Real-World Expertise", text: "Our team has firsthand experience in threat identification, mitigation, and response in dynamic, unpredictable environments." },
  { title: "Elite Instruction & Credentials", text: "Our instructors are black belts in Krav Maga, certified train-the-trainer professionals, and subject matter experts in practical self-defense." },
  { title: "Advanced Threat Mitigation", text: "We don't just train your team—we identify risks, close security gaps, and strengthen your overall safety posture." },
  { title: "Operational Mindset Training", text: "We teach participants how to think, react, and make decisions under pressure—skills developed through real operational experience." },
  { title: "Tailored, Not Generic", text: "Every program is customized to your environment, your people, and your specific risks. No two trainings are the same." },
  { title: "Engaging, Hands-On Delivery", text: "Our sessions are interactive, practical, and designed to build confidence—not fear." },
];

const PACKAGE_DATA = [
  {
    title: "Basic Awareness",
    icon: Eye,
    duration: "60-90 minutes",
    format: "Presentation-based awareness session",
    idealFor: "Intro sessions, onboarding programs, HR initiatives, and organizations beginning to strengthen safety awareness.",
    overview: "A focused awareness session designed to introduce core preparedness concepts through classroom-style instruction, discussion, Q&A, and light demonstrations when appropriate.",
    includes: ["Threat recognition", "Situational awareness", "Warning signs of violence", "Basic response options", "Emergency mindset", "Q&A with staff"],
    outcome: "Employees leave with a stronger awareness mindset and a better understanding of what to look for before a situation becomes dangerous.",
  },
  {
    title: "Core Training",
    icon: ShieldCheck,
    duration: "2-3 hours",
    format: "Education + hands-on practical training",
    idealFor: "Organizations ready to build practical preparedness, response capability, and staff confidence during emergencies.",
    overview: "A practical training experience that combines education, discussion, and hands-on skill development for organizations that want meaningful readiness without committing to a full-day session.",
    includes: ["Threat recognition", "Situational awareness", "Basic active threat response principles", "Hands-on defensive concepts", "Escaping dangerous situations", "Improvised defensive tools", "Stress response strategies", "First responder coordination basics"],
    outcome: "Participants leave with practical skills they can actually use, bridging the gap between knowing what to do and being able to act under stress.",
  },
  {
    title: "Full-Day Training",
    icon: Activity,
    duration: "6-8 hours",
    format: "Hands-on training + scenarios",
    idealFor: "Organizations seeking complete team preparedness, leadership coordination, and scenario-based response training.",
    overview: "A deeper one-day training experience that expands Core Training with scenario-based drills, leadership response, staff coordination, and realistic decision-making under pressure.",
    includes: ["Active threat education", "Workplace violence prevention", "Threat recognition", "Hands-on defensive skills", "Stress response training", "Scenario-based drills", "Leadership response", "Communication during emergencies", "Staff coordination", "First responder coordination"],
    outcome: "The organization leaves with a more coordinated response capability. Staff and leadership better understand their roles, how to communicate, and how to respond under pressure.",
  },
  {
    title: "Flagship Program",
    icon: Crosshair,
    duration: "8+ hours",
    format: "Training + assessment + leadership strategy",
    idealFor: "Large organizations, campuses, and leadership teams seeking comprehensive preparedness and organization-wide readiness.",
    overview: "A premium preparedness package that combines training, consulting, leadership strategy, scenario-based exercises, and organizational safety assessment.",
    includes: ["Full-day staff training", "Scenario-based exercises", "Leadership response training", "Safety assessment", "Policy review", "Written recommendations", "Executive briefing", "Optional digital training access"],
    outcome: "Your team leaves with practical training, stronger leadership coordination, a clearer emergency response plan, and actionable recommendations to reduce risk.",
  },
  {
    title: "Annual Safety Program",
    icon: CalendarIcon,
    duration: "Year-long readiness partnership",
    format: "Training + refreshers + advisory support",
    idealFor: "Organizations seeking ongoing readiness, refresher training, onboarding support, and long-term preparedness.",
    overview: "A recurring safety relationship designed to keep preparedness active through annual training, refreshers, onboarding support, and continued guidance.",
    includes: ["Annual training day", "Quarterly refreshers", "New employee onboarding support", "Policy updates", "Emergency planning consultation", "Continued safety guidance"],
    outcome: "Safety becomes part of the organization’s culture instead of a once-a-year checkbox.",
  },
  {
    title: "Safety Assessment",
    icon: ClipboardCheck,
    duration: "Half-day/full-day + written recommendations",
    format: "Consulting assessment",
    idealFor: "Organizations that want to identify vulnerabilities, strengthen policies, and improve overall security posture.",
    overview: "A focused consulting service that evaluates the organization’s current safety posture and identifies practical opportunities to reduce risk.",
    includes: ["Facility walk-through", "Entry and exit review", "Policy review", "Vulnerability identification", "Emergency procedure review", "Staff and leadership discussion", "Written recommendations", "Leadership briefing"],
    outcome: "Leaders receive a clear picture of vulnerabilities and practical recommendations to reduce risk before an incident happens.",
  },
];

const InstagramIcon = ({ size = 21 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="2.2" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2.2" />
    <circle cx="17.4" cy="6.6" r="1.2" fill="currentColor" />
  </svg>
);

const FacebookIcon = ({ size = 21 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M14.2 8.1h2.1V4.7c-.4-.1-1.6-.2-3-.2-3 0-5 1.8-5 5.2v2.9H5v3.8h3.3V22h4v-5.6h3.3l.5-3.8h-3.8V10c0-1.1.3-1.9 1.9-1.9Z"
      fill="currentColor"
    />
  </svg>
);

const SOCIAL_LINKS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/modernthreatconsulting/",
    icon: InstagramIcon,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61583016827552",
    icon: FacebookIcon,
  },
];

const Reveal = ({ children, delay = 0, y = 28, once = true, className, style, as = "div" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-60px" });
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;

  if (reduce) {
    return <div className={className} style={style}>{children}</div>;
  }

  return (
    <MotionTag
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.7, delay, ease: EASE }}
      className={className}
      style={style}
    >
      {children}
    </MotionTag>
  );
};

const StaggerGroup = ({ children, className, style, delayChildren = 0.1, stagger = 0.08 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className} style={style}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: stagger, delayChildren } } }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
};

const staggerItem = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
};

const Divider = () => (
  <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${C.zinc800}, transparent)` }} />
);

const Tag = ({ children }) => (
  <span style={{
    fontSize: 15,
    fontWeight: 900,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: C.red,
    fontFamily: "'Barlow Condensed', sans-serif",
  }}>
    {children}
  </span>
);

const Btn = ({ children, variant = "primary", onClick, type = "button", disabled, style = {}, className }) => {
  const [hov, setHov] = useState(false);
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "14px 28px",
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    outline: "none",
    position: "relative",
  };
  const variants = {
    primary: {
      background: hov ? C.redLight : C.red,
      color: C.white,
      boxShadow: hov ? `0 8px 32px ${C.red}55` : `0 4px 16px ${C.red}33`,
    },
    secondary: {
      background: hov ? C.zinc800 : C.zinc900,
      color: C.white,
      border: `1px solid ${C.zinc700}`,
    },
    program: {
      background: hov
        ? C.red
        : `linear-gradient(135deg, ${C.red}24 0%, rgba(24,24,27,0.96) 55%, rgba(0,0,0,0.94) 100%)`,
      color: C.white,
      border: `2px solid ${hov ? C.redLight : C.red}`,
      boxShadow: hov
        ? `0 14px 38px ${C.red}55, inset 0 0 0 1px rgba(255,255,255,0.16)`
        : `0 8px 30px ${C.red}22, inset 0 0 0 1px rgba(255,255,255,0.08)`,
    },
    ghost: {
      background: "transparent",
      color: hov ? C.red : C.zinc400,
      padding: "10px 0",
    },
  };

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled}
      whileHover={{ y: variant === "ghost" ? 0 : -1 }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.15 }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={className}
      style={{ ...base, ...variants[variant], ...style, opacity: disabled ? 0.5 : 1 }}
    >
      {children}
    </motion.button>
  );
};

const Logo = ({ size = "md" }) => {
  const h = size === "sm" ? 84 : 110;
  const textSize = size === "sm" ? 18 : 25;
  const subTextSize = size === "sm" ? 11 : 15;

  return (
    <div className="site-logo" style={{ display: "flex", alignItems: "center", gap: 18 }}>
      <img
        className="site-logo-mark"
        src="/logo.png"
        alt="Modern Threat Consulting"
        style={{
          height: h,
          width: h,
          objectFit: "contain",
          filter: "drop-shadow(0 0 16px rgba(185,28,28,0.32))",
          flexShrink: 0,
        }}
      />
      <div style={{ lineHeight: 1.1 }}>
        <div className="site-logo-wordmark" style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: textSize,
          fontWeight: 900,
          color: C.white,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontStyle: "italic",
          whiteSpace: "nowrap",
        }}>
          <span>MODERN </span>
          <span style={{ color: C.redLight }}>THREAT</span>
        </div>
        <div className="site-logo-submark" style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: subTextSize,
          fontWeight: 900,
          color: C.white,
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}>
          CONSULTING
        </div>
      </div>
    </div>
  );
};

const NavLink = ({ label, active, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        fontFamily: "'Barlow', sans-serif",
        fontSize: 14,
        fontWeight: 800,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: active ? C.red : hov ? C.redLight : "#e4e4e7",
        transition: "color 0.18s",
        padding: "4px 0",
        whiteSpace: "nowrap",
        position: "relative",
      }}
    >
      {label}
      {active && (
        <motion.div
          layoutId="nav-underline"
          style={{ position: "absolute", bottom: -4, left: 0, right: 0, height: 2, background: C.red }}
        />
      )}
    </button>
  );
};

const DropdownIndustryItem = ({ cat, active, currentPage, navigate, setIndustriesOpen, setIndustriesHov }) => {
  const [hov, setHov] = useState(false);
  const Icon = cat.icon;

  return (
    <button
      onClick={() => {
        navigate(cat.id);
        setIndustriesOpen(false);
        setIndustriesHov(false);
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        width: "100%",
        padding: "18px 24px",
        background: active ? C.red : hov ? C.red : "transparent",
        border: "none",
        borderBottom: `1px solid ${hov || active ? C.redLight : C.zinc800}`,
        cursor: "pointer",
        transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
        fontFamily: "'Barlow', sans-serif",
        fontSize: 18,
        fontWeight: 800,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: active || hov ? C.white : C.zinc300,
        textAlign: "left",
        boxShadow: hov || active ? `inset 5px 0 0 ${C.redLight}, 0 0 24px rgba(185,28,28,0.28)` : "none",
      }}
    >
      <Icon size={22} color={active || hov ? C.white : C.zinc300} strokeWidth={2.3} />
      <span style={{ lineHeight: 1.15 }}>{cat.title}</span>
      {currentPage === cat.id && <div style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: C.redLight }} />}
    </button>
  );
};

const Header = ({ currentPage, navigate, openModal }) => {
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [industriesHov, setIndustriesHov] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileIndustriesOpen(false);
  }, [currentPage]);

  const isIndustry = !!CATEGORY_DATA[currentPage];
  const mobilePrimaryItems = [
    { label: "Our Expertise", page: "expertise" },
    { label: "Preparedness Programs", page: "programs" },
  ];
  const mobileSectorItems = Object.values(CATEGORY_DATA).map((cat) => ({ label: cat.title, page: cat.id }));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@0,400;0,600;0,700;0,900;1,700;1,900&family=Barlow:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; }
        html, body, #root { width: 100%; max-width: 100%; overflow-x: hidden; }
        body { margin: 0; background: #000; }
        img, video, canvas { max-width: 100%; }
        button, input, textarea { font: inherit; }
        .mobile-back-home { display: none !important; }
        .mobile-actions { display: none; }
        .mobile-contact-inline { display: inline-flex; }
        .hero-copy, .readable-copy { text-wrap: pretty; overflow-wrap: break-word; }
        .site-logo-wordmark, .site-logo-submark, .page-hero-title, .hero-title { overflow-wrap: normal; }
        @media (max-width: 1180px) {
          .desktop-nav { display: none !important; }
          .mobile-actions { display: flex; }
          .grid-2, .grid-4, .grid-main { grid-template-columns: 1fr !important; }
          .grid-3 { grid-template-columns: 1fr 1fr !important; }
          .footer-grid { grid-template-columns: 1fr !important; }
          .section-pad { padding: 64px 20px !important; }
          .card-pad { padding: 28px !important; }
        }
        @media (max-width: 860px) {
          .mobile-contact-inline { display: none !important; }
          .site-logo { gap: 13px !important; min-width: 0 !important; }
          .site-logo-mark { height: 86px !important; width: 86px !important; }
          .site-logo-wordmark { font-size: 22px !important; letter-spacing: 0.065em !important; }
          .site-logo-submark { font-size: 13px !important; letter-spacing: 0.24em !important; }
        }
        @media (max-width: 760px) {
          .grid-3 { grid-template-columns: 1fr !important; }
          .site-header-inner { padding: 0 16px !important; height: 104px !important; }
          .mobile-menu-panel { max-height: calc(100vh - 104px) !important; }
          .mobile-back-home { display: inline-flex !important; }
          .page-hero-title { font-size: clamp(48px, 14vw, 82px) !important; line-height: 0.94 !important; }
          .section-pad { padding-left: 18px !important; padding-right: 18px !important; }
        }
        @media (max-width: 620px) {
          .hero-title { font-size: clamp(48px, 15vw, 58px) !important; line-height: 0.94 !important; }
          .modal-panel { padding: 32px 22px !important; }
          .form-grid { grid-template-columns: 1fr !important; }
          .mobile-hide { display: none !important; }
          .site-logo { gap: 10px !important; }
          .site-logo-mark { height: 78px !important; width: 78px !important; }
          .site-logo-wordmark { font-size: 19px !important; letter-spacing: 0.045em !important; }
          .site-logo-submark { font-size: 11px !important; letter-spacing: 0.2em !important; }
          .section-pad { padding: 54px 16px !important; }
          .card-pad { padding: 24px !important; min-height: auto !important; }
          .package-title { min-height: auto !important; margin-bottom: 20px !important; }
          .compare-table-wrap table { min-width: 760px !important; }
          .readable-copy { font-size: 17px !important; }
          .mobile-menu-panel { padding-left: 18px !important; padding-right: 18px !important; }
          .hero-cta-row > button, .mobile-full-button { width: 100% !important; justify-content: center !important; white-space: normal !important; }
          button { max-width: 100%; }
        }
        @media (max-width: 430px) {
          .site-header-inner { padding: 0 12px !important; }
          .site-logo-mark { height: 64px !important; width: 64px !important; }
          .site-logo-wordmark { font-size: 16px !important; letter-spacing: 0.035em !important; }
          .site-logo-submark { font-size: 9px !important; letter-spacing: 0.17em !important; }
          .page-hero-title { font-size: clamp(42px, 13vw, 56px) !important; }
          .hero-title { font-size: clamp(42px, 14vw, 52px) !important; }
          .site-logo { max-width: calc(100vw - 82px) !important; }
          .section-pad { padding-left: 14px !important; padding-right: 14px !important; }
        }
      `}</style>

      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: EASE }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: scrolled ? "rgba(0,0,0,0.97)" : "rgba(0,0,0,0.92)",
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${scrolled ? C.zinc800 : "transparent"}`,
          transition: "background 0.3s ease, border-color 0.3s ease",
        }}
      >
        <div className="site-header-inner" style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 116, gap: 14 }}>
          <div style={{ cursor: "pointer", flexShrink: 0 }} onClick={() => navigate("home")}>
            <Logo size="md" />
          </div>

          <nav className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "nowrap" }}>
            <NavLink label="Home" active={currentPage === "home"} onClick={() => navigate("home")} />

            <div
              style={{ position: "relative" }}
              onMouseEnter={() => {
                clearTimeout(timerRef.current);
                setIndustriesOpen(true);
                setIndustriesHov(true);
              }}
              onMouseLeave={() => {
                timerRef.current = setTimeout(() => {
                  setIndustriesOpen(false);
                  setIndustriesHov(false);
                }, 150);
              }}
            >
              <button style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "'Barlow', sans-serif",
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: isIndustry ? C.red : industriesHov ? C.redLight : "#e4e4e7",
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 0",
                transition: "color 0.18s",
                whiteSpace: "nowrap",
              }}>
                Industries <ChevronDown size={13} style={{ transition: "transform 0.2s", transform: industriesOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
              </button>

              <AnimatePresence>
                {industriesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18, ease: EASE }}
                    style={{
                      position: "absolute",
                      top: "calc(100% + 14px)",
                      right: 0,
                      width: 620,
                      maxWidth: "calc(100vw - 24px)",
                      background: "#17171a",
                      border: `1px solid ${C.zinc800}`,
                      borderRadius: 8,
                      boxShadow: "0 24px 48px rgba(0,0,0,0.6)",
                      padding: "8px 0",
                      overflow: "hidden",
                      zIndex: 1000,
                    }}
                  >
                    {Object.values(CATEGORY_DATA).map((cat) => {
                      const active = currentPage === cat.id;
                      return (
                        <DropdownIndustryItem
                          key={cat.id}
                          cat={cat}
                          active={active}
                          currentPage={currentPage}
                          navigate={navigate}
                          setIndustriesOpen={setIndustriesOpen}
                          setIndustriesHov={setIndustriesHov}
                        />
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavLink label="Our Expertise" active={currentPage === "expertise"} onClick={() => navigate("expertise")} />
            <NavLink label="Preparedness Programs" active={currentPage === "programs"} onClick={() => navigate("programs")} />

            <Btn onClick={openModal} variant="primary" style={{
              padding: "14px 28px",
              fontFamily: "'Barlow', sans-serif",
              fontSize: 14,
              fontWeight: 800,
              letterSpacing: "0.1em",
              whiteSpace: "nowrap",
            }}>
              Contact Us
            </Btn>
          </nav>

          <div className="mobile-actions" style={{ alignItems: "center", gap: 10 }}>
            <button
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              onClick={() => {
                setMobileOpen((open) => !open);
                if (mobileOpen) setMobileIndustriesOpen(false);
              }}
              style={{
                width: 46,
                height: 46,
                display: "grid",
                placeItems: "center",
                background: C.zinc900,
                border: `1px solid ${C.zinc700}`,
                color: C.white,
                cursor: "pointer",
              }}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <Btn className="mobile-contact-inline" onClick={openModal} variant="primary" style={{ padding: "12px 16px", fontSize: 12 }}>
              Contact
            </Btn>
          </div>
        </div>
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              className="mobile-menu-panel"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, ease: EASE }}
              style={{
                padding: "24px 22px 26px",
                background: "linear-gradient(180deg, rgba(5,5,6,0.99), rgba(12,12,14,0.99))",
                borderTop: `1px solid ${C.zinc800}`,
                maxHeight: "calc(100vh - 116px)",
                overflowY: "auto",
                boxShadow: "0 24px 60px rgba(0,0,0,0.65)",
              }}
            >
              <MobileMenuButton label="Home" active={currentPage === "home"} onClick={() => navigate("home")} />

              <div style={{ margin: "16px 0 10px" }}>
                <button
                  onClick={() => setMobileIndustriesOpen((open) => !open)}
                  aria-expanded={mobileIndustriesOpen}
                  style={{
                    width: "100%",
                    padding: "16px 0",
                    background: isIndustry ? `linear-gradient(90deg, ${C.red}24, rgba(225,6,0,0.05))` : "transparent",
                    border: "none",
                    borderLeft: isIndustry ? `4px solid ${C.red}` : "4px solid transparent",
                    color: isIndustry || mobileIndustriesOpen ? C.white : C.zinc300,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: "clamp(18px, 4.8vw, 24px)",
                    fontWeight: 900,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    lineHeight: 1.25,
                    textAlign: "left",
                    transition: "color 0.18s ease, background 0.18s ease, border-color 0.18s ease",
                  }}
                >
                  <span>Industries</span>
                  <ChevronDown
                    size={22}
                    style={{
                      color: mobileIndustriesOpen ? C.redLight : "currentColor",
                      flexShrink: 0,
                      transition: "transform 0.2s ease, color 0.2s ease",
                      transform: mobileIndustriesOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {mobileIndustriesOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0, y: -6 }}
                      animate={{ opacity: 1, height: "auto", y: 0 }}
                      exit={{ opacity: 0, height: 0, y: -6 }}
                      transition={{ duration: 0.22, ease: EASE }}
                      style={{
                        overflow: "hidden",
                        marginTop: 8,
                        background: "rgba(255,255,255,0.035)",
                        border: `1px solid ${C.zinc800}`,
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {mobileSectorItems.map((item) => (
                          <MobileMenuButton
                            key={item.page}
                            label={item.label}
                            active={currentPage === item.page}
                            onClick={() => navigate(item.page)}
                            variant="sector"
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingTop: 8 }}>
                {mobilePrimaryItems.map((item) => (
                  <MobileMenuButton
                    key={item.page}
                    label={item.label}
                    active={currentPage === item.page}
                    onClick={() => navigate(item.page)}
                  />
                ))}
              </div>

              <Btn onClick={openModal} variant="primary" style={{
                width: "100%",
                marginTop: 28,
                padding: "18px 20px",
                fontFamily: "'Barlow', sans-serif",
                fontSize: 17,
                fontWeight: 900,
                letterSpacing: "0.18em",
                boxShadow: `0 12px 32px ${C.red}35`,
              }}>
                Contact Us
              </Btn>
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

const MobileMenuButton = ({ label, active, onClick, variant = "primary" }) => {
  const isSector = variant === "sector";
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        maxWidth: "none",
        padding: isSector ? "15px 18px" : "11px 0",
        background: active ? `linear-gradient(90deg, ${C.red}24, rgba(225,6,0,0.05))` : "transparent",
        border: "none",
        borderBottom: isSector ? `1px solid ${C.zinc800}` : "none",
        borderLeft: active ? `4px solid ${C.red}` : "4px solid transparent",
        color: active ? C.white : isSector ? C.zinc300 : C.white,
        cursor: "pointer",
        fontFamily: "'Barlow', sans-serif",
        fontSize: isSector ? "clamp(13px, 3.5vw, 17px)" : "clamp(18px, 4.8vw, 24px)",
        fontWeight: 900,
        letterSpacing: isSector ? "0.11em" : "0.2em",
        textTransform: "uppercase",
        lineHeight: 1.25,
        textAlign: "left",
        transition: "color 0.18s ease, background 0.18s ease, border-color 0.18s ease",
      }}
    >
      {label}
    </button>
  );
};

const AnimatedHeroBackground = ({ scrollY }) => {
  const reduce = useReducedMotion();
  const bgY = useTransform(scrollY, [0, 800], [0, 120]);
  const glowOpacity = useTransform(scrollY, [0, 600], [1, 0.3]);

  return (
    <>
      <div style={{ position: "absolute", inset: 0, background: "#050a0e" }} />
      <motion.div style={{ position: "absolute", inset: -40, y: reduce ? 0 : bgY }}>
        <motion.div
          animate={reduce ? {} : { backgroundPositionX: ["0px", "120px"], backgroundPositionY: ["0px", "120px"] }}
          transition={reduce ? {} : { duration: 60, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.18,
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(`
              <svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>
                <g stroke='#4ade80' stroke-width='0.8' fill='none'>
                  <line x1='0' y1='30' x2='40' y2='30'/>
                  <line x1='60' y1='30' x2='120' y2='30'/>
                  <line x1='0' y1='90' x2='30' y2='90'/>
                  <line x1='70' y1='90' x2='120' y2='90'/>
                  <line x1='40' y1='0' x2='40' y2='120'/>
                  <line x1='80' y1='0' x2='80' y2='120'/>
                  <circle cx='40' cy='30' r='3'/>
                  <circle cx='80' cy='90' r='3'/>
                  <rect x='52' y='42' width='16' height='16'/>
                </g>
              </svg>
            `)}")`,
            backgroundRepeat: "repeat",
          }}
        />
      </motion.div>

      {!reduce && [0, 1.2, 2.4, 0.6, 1.8, 3].map((delay, i) => (
        <motion.div
          key={i}
          animate={{ opacity: [0, 0.8, 0], scale: [0.5, 1.5, 0.5] }}
          transition={{ duration: 3, delay, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            left: `${15 + i * 13}%`,
            top: `${25 + (i % 3) * 20}%`,
            width: 6,
            height: 6,
            background: C.red,
            borderRadius: "50%",
            boxShadow: `0 0 12px ${C.red}, 0 0 24px ${C.red}88`,
          }}
        />
      ))}

      <motion.div
        animate={reduce ? {} : { opacity: [0.55, 0.75, 0.55], scale: [1, 1.05, 1] }}
        transition={reduce ? {} : { duration: 8, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "55%",
          height: "70%",
          background: "radial-gradient(ellipse at center, rgba(20,80,80,0.55) 0%, rgba(0,40,60,0.3) 45%, transparent 75%)",
          pointerEvents: "none",
          opacity: glowOpacity,
        }}
      />

      {!reduce && (
        <motion.div
          animate={{ top: ["0%", "100%"] }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${C.red}55 20%, ${C.red}88 50%, ${C.red}55 80%, transparent)`,
            boxShadow: `0 0 20px ${C.red}66`,
            pointerEvents: "none",
            mixBlendMode: "screen",
          }}
        />
      )}

      <div style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(105deg, ${C.black} 30%, rgba(0,0,0,0.6) 55%, rgba(0,0,0,0.15) 100%)`,
        pointerEvents: "none",
      }} />
    </>
  );
};

const HomePage = ({ navigate, openModal }) => {
  const { scrollY } = useScroll();
  const reduce = useReducedMotion();
  const textY = useTransform(scrollY, [0, 600], [0, -80]);
  const textOpacity = useTransform(scrollY, [0, 400], [1, 0.3]);

  return (
    <div>
      <section style={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", background: C.black, overflow: "hidden" }}>
        <AnimatedHeroBackground scrollY={scrollY} />
        <motion.div
          style={{
            position: "relative",
            maxWidth: 1280,
            margin: "0 auto",
            padding: "120px 24px 80px",
            width: "100%",
            y: reduce ? 0 : textY,
            opacity: reduce ? 1 : textOpacity,
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: EASE }} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.4, ease: EASE }} style={{ height: 2, width: 48, background: C.red, transformOrigin: "left" }} />
              <Tag>Mission Statement</Tag>
            </motion.div>

            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: EASE }}
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 900,
                fontStyle: "italic",
                fontSize: "clamp(72px, 11vw, 130px)",
                color: C.white,
                lineHeight: 0.88,
                textTransform: "uppercase",
                letterSpacing: 0,
                margin: "0 0 32px",
              }}
            >
              <span style={{ display: "block" }}>THREAT</span>
              <span style={{ display: "block", color: C.zinc500 }}>TRAINING</span>
              <span style={{ display: "block" }}>REINVENTED.</span>
            </motion.h1>

            <motion.p className="hero-copy" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85, ease: EASE }} style={{ fontFamily: "'Barlow', sans-serif", fontSize: "clamp(18px, 2.1vw, 22px)", color: "#e5e7eb", lineHeight: 1.75, fontWeight: 400, maxWidth: 720, marginBottom: 44 }}>
              At Modern Threat Consulting LLC, we provide comprehensive, reality-based training designed to prepare organizations for the unthinkable. Our mission is to equip individuals at every level across diverse environments with the knowledge, confidence, and practical skills needed to recognize threats, make informed decisions, and respond effectively in high-stress situations—whether working alone, operating in unfamiliar environments, or facing an active shooter or active killing event.
            </motion.p>

            <motion.div className="hero-cta-row" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1, ease: EASE }} style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Btn onClick={openModal} variant="primary" style={{
                padding: "18px 38px",
                fontFamily: "'Barlow', sans-serif",
                fontSize: 16,
                fontWeight: 800,
                letterSpacing: "0.1em",
              }}>
                Schedule Your Training <ArrowRight size={15} />
              </Btn>
              <Btn onClick={() => navigate("programs")} variant="program" style={{
                padding: "18px 38px",
                fontFamily: "'Barlow', sans-serif",
                fontSize: 16,
                fontWeight: 800,
                letterSpacing: "0.1em",
                backdropFilter: "blur(6px)",
              }}>
                Preparedness Programs <ArrowRight size={15} />
              </Btn>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <section className="section-pad" style={{ background: "linear-gradient(180deg, #050506 0%, #09090b 100%)", padding: "86px 24px", borderTop: `1px solid ${C.zinc900}`, borderBottom: `1px solid ${C.zinc900}` }}>
        <Reveal>
          <div className="grid-main" style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "5fr 7fr", gap: 56, alignItems: "start" }}>
            <div>
              <Tag>Who We Serve</Tag>
              <h2 style={{ ...sectionHead, marginBottom: 0 }}>Customized Training for Diverse Organizations</h2>
            </div>
            <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 19, color: C.zinc300, lineHeight: 1.85, margin: 0 }}>
              Every organization faces different risks, routines, and responsibilities. We work with public and private institutions, healthcare and corporate environments, government and high-security teams, transportation and travel operations, event and venue staff, and professionals who work in unpredictable settings. For teams looking to grow stronger together, our Wellness & Team Building programs create space for connection, confidence, and camaraderie that carry over into everyday work. Every program is thoughtfully designed around the unique needs, goals, and day-to-day realities of the people we serve.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="section-pad" style={{ background: C.zinc950, padding: "100px 24px" }}>
        <div className="grid-main" style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 2fr", gap: 64, alignItems: "start" }}>
          <Reveal>
            <Tag>The Advantage</Tag>
            <h2 style={sectionHead}>Expertise Driven by Reality</h2>
            <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", color: C.zinc300, lineHeight: 1.75, fontSize: 18, marginBottom: 32 }}>
              We move beyond theory to provide actionable intelligence and practical physical responses tailored to your specific facility and personnel.
            </p>
            <Btn variant="ghost" onClick={() => navigate("expertise")} style={{ display: "flex", alignItems: "center", gap: 8, color: C.redLight, fontSize: 14 }}>
              Full Expertise Overview <ArrowRight size={13} />
            </Btn>
          </Reveal>
          <StaggerGroup className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {EXPERTISE_SECTIONS.map((exp) => (
              <motion.div key={exp.title} variants={staggerItem}>
                <InfoCard icon={exp.icon} title={exp.title} desc={exp.description} />
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <Divider />

      <section className="section-pad" style={{ background: "#0a0a0a", padding: "100px 24px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <Tag>Core Industries</Tag>
              <h2 style={{ ...sectionHead, textAlign: "center", marginTop: 12 }}>Who We Prepare</h2>
            </div>
          </Reveal>
          <StaggerGroup className="grid-4" stagger={0.06} style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gridAutoRows: "1fr", alignItems: "stretch", gap: 16 }}>
            {Object.values(CATEGORY_DATA).map((cat) => (
              <motion.div key={cat.id} variants={staggerItem} style={{ height: "100%" }}>
                <SectorCard cat={cat} onClick={() => navigate(cat.id)} />
              </motion.div>
            ))}
          </StaggerGroup>
          <Reveal y={24}>
            <div style={{
              marginTop: 44,
              padding: "34px 38px",
              background: "linear-gradient(135deg, rgba(225,6,0,0.13), rgba(24,24,27,0.92) 48%, rgba(0,0,0,0.92))",
              border: `1px solid ${C.zinc800}`,
              borderLeft: `5px solid ${C.red}`,
              boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
              display: "grid",
              gridTemplateColumns: "7fr 3fr",
              gap: 28,
              alignItems: "center",
            }} className="grid-main">
              <div>
                <h3 style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: "clamp(30px, 3.2vw, 44px)",
                  fontWeight: 900,
                  fontStyle: "italic",
                  color: C.white,
                  textTransform: "uppercase",
                  letterSpacing: 0,
                  lineHeight: 0.98,
                  margin: "0 0 16px",
                }}>
                  Don’t See Your Industry?
                </h3>
                <p className="readable-copy" style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: 18,
                  color: C.zinc300,
                  lineHeight: 1.75,
                  margin: 0,
                  maxWidth: 860,
                }}>
                  Every organization faces different risks. If your industry is not listed, we can build customized training around your people, environment, and specific concerns, from private safety sessions for students heading to college to specialized programs for teams operating in unique settings.
                </p>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Btn className="mobile-full-button" onClick={openModal} variant="primary" style={{
                  padding: "18px 30px",
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: 15,
                  fontWeight: 900,
                  letterSpacing: "0.1em",
                  whiteSpace: "nowrap",
                }}>
                  Request Custom Training <ArrowRight size={15} />
                </Btn>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand onContact={openModal} variant="home" />
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, desc }) => {
  const [hov, setHov] = useState(false);
  return (
    <motion.div
      className="card-pad"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.25, ease: EASE }}
      style={{
        background: C.zinc900,
        padding: 40,
        border: `1px solid ${hov ? C.redLight : C.zinc800}`,
        transition: "border-color 0.25s ease",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Icon size={34} color={C.redLight} style={{ marginBottom: 22 }} />
      <h4 style={smallHead}>{title}</h4>
      <p style={smallBody}>{desc}</p>
    </motion.div>
  );
};

const SectorCard = ({ cat, onClick }) => {
  const [hov, setHov] = useState(false);
  const Icon = cat.icon;

  return (
    <motion.div
      className="card-pad"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25, ease: EASE }}
      style={{
        background: hov ? "#1e1e21" : "#161618",
        border: `1px solid ${hov ? C.redLight : "#2e2e32"}`,
        padding: "38px 34px 32px",
        cursor: "pointer",
        transition: "background 0.25s ease, border-color 0.25s ease",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: 300,
        height: "100%",
        boxShadow: hov ? "0 12px 40px rgba(0,0,0,0.5)" : "none",
      }}
    >
      <div>
        <Icon size={38} color={C.redLight} style={{ marginBottom: 34 }} />
        <h4 style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: 24,
          fontWeight: 900,
          color: C.white,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          lineHeight: 1.15,
          marginBottom: 24,
        }}>
          {cat.title}
        </h4>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 900, letterSpacing: "0.14em", textTransform: "uppercase", color: hov ? C.redLight : C.zinc300, transition: "color 0.2s" }}>
        View Industry <ArrowRight size={12} />
      </div>
    </motion.div>
  );
};

const CapabilitiesPage = ({ openModal }) => (
  <div style={{ paddingTop: 116, background: C.zinc950, minHeight: "100vh" }}>
    <PageHero
      tag="Service Catalog"
      title={<>TRAINING &<br /><span style={{ color: C.zinc500 }}>CONSULTING.</span></>}
      subtitle="Our capabilities span the full spectrum of organizational safety—from initial audits to high-stress reality-based response drills and wellness initiatives."
    />

    <section className="section-pad" style={{ padding: "80px 24px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", flexDirection: "column", gap: 80 }}>
        {CAPABILITIES_DATA.map((cap, i) => {
          const Icon = cap.icon;
          const fromLeft = i % 2 === 0;
          return (
            <motion.div
              key={cap.title}
              className="grid-main"
              initial={{ opacity: 0, x: fromLeft ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE }}
              style={{
                display: "grid",
                gridTemplateColumns: "5fr 7fr",
                gap: 64,
                alignItems: "start",
                paddingBottom: 80,
                borderBottom: i < CAPABILITIES_DATA.length - 1 ? `1px solid ${C.zinc900}` : "none",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                  <div style={{ padding: 16, background: `${C.red}15`, border: `1px solid ${C.red}30` }}>
                    <Icon size={28} color={C.red} />
                  </div>
                  <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontStyle: "italic", fontSize: 52, fontWeight: 900, color: `${C.red}55` }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 style={midHead}>{cap.title}</h3>
                {cap.description && <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, color: C.zinc300, lineHeight: 1.75, marginBottom: 24 }}>{cap.description}</p>}
                <div style={{ borderLeft: `3px solid ${C.red}`, padding: "16px 20px", background: "rgba(255,255,255,0.03)" }}>
                  <p style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 900, color: C.redLight, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Primary Benefit</p>
                  <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 17, fontStyle: "italic", color: C.white, lineHeight: 1.65, margin: 0 }}>{cap.benefit}</p>
                </div>
              </div>
              <FeatureList points={cap.points} />
            </motion.div>
          );
        })}
      </div>
    </section>

    <CtaBand onContact={openModal} />
  </div>
);

const PreparednessProgramsPage = ({ navigate, openModal }) => {
  const [compareOpen, setCompareOpen] = useState(false);

  return (
  <div style={{ paddingTop: 116, background: C.zinc950, minHeight: "100vh" }}>
    <PageHero
      navigate={navigate}
      showMobileBack
      tag="Training & Consulting"
      title={<>PREPAREDNESS<br /><span style={{ color: C.zinc500 }}>PROGRAMS.</span></>}
      subtitle="Scalable training options designed to prepare organizations before violence occurs."
    />

    <section className="section-pad" style={{ padding: "84px 24px 72px", background: "linear-gradient(180deg, #09090b 0%, #0d0d10 100%)" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <Reveal>
          <div style={{
            borderLeft: `4px solid ${C.red}`,
            background: "rgba(255,255,255,0.035)",
            padding: "26px 30px",
            margin: "0 0 56px",
            maxWidth: 1000,
          }}>
            <p className="readable-copy" style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: 20,
              color: C.zinc300,
              lineHeight: 1.8,
              margin: 0,
            }}>
              Modern Threat Consulting LLC provides reality-based preparedness programs that help organizations improve awareness, strengthen decision-making under stress, and build practical response capability. Each program level is designed to support workplace safety, active threat preparedness, leadership readiness, and long-term organizational resilience.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div style={{ maxWidth: 820, marginBottom: 56 }}>
            <Tag>Training & Consulting Capabilities</Tag>
            <h2 style={{ ...sectionHead, marginBottom: 24 }}>How We Build Readiness</h2>
            <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 19, color: C.zinc300, lineHeight: 1.8, margin: 0 }}>
              These capabilities form the foundation of every preparedness package and can be tailored to your organization’s risks, people, policies, and operating environment.
            </p>
          </div>
        </Reveal>

        <div style={{ display: "flex", flexDirection: "column", gap: 64 }}>
          {CAPABILITIES_DATA.map((cap, i) => {
            const Icon = cap.icon;
            return (
              <Reveal key={cap.title} delay={i * 0.04}>
                <div
                  className="grid-main"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "5fr 7fr",
                    gap: 56,
                    alignItems: "start",
                    paddingBottom: 64,
                    borderBottom: i < CAPABILITIES_DATA.length - 1 ? `1px solid ${C.zinc900}` : "none",
                  }}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                      <div style={{ padding: 16, background: `${C.red}15`, border: `1px solid ${C.red}30` }}>
                        <Icon size={28} color={C.red} />
                      </div>
                      <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontStyle: "italic", fontSize: 52, fontWeight: 900, color: `${C.red}55` }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 style={midHead}>{cap.title}</h3>
                    {cap.description && <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, color: C.zinc300, lineHeight: 1.75, marginBottom: 24 }}>{cap.description}</p>}
                    <div style={{ borderLeft: `3px solid ${C.red}`, padding: "16px 20px", background: "rgba(255,255,255,0.03)" }}>
                      <p style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 900, color: C.redLight, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Primary Benefit</p>
                      <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 17, fontStyle: "italic", color: C.white, lineHeight: 1.65, margin: 0 }}>{cap.benefit}</p>
                    </div>
                  </div>
                  <FeatureList points={cap.points} />
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <section className="section-pad" style={{ padding: "78px 24px 86px", background: C.zinc950, borderTop: `1px solid ${C.zinc900}` }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <Reveal>
          <div style={{ maxWidth: 820, marginBottom: 44 }}>
              <Tag>Packages</Tag>
              <h2 style={{ ...sectionHead, margin: "12px 0 22px" }}>Training Options Built Around Readiness</h2>
              <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 19, color: C.zinc300, lineHeight: 1.8, margin: 0 }}>
                Choose the level of support that fits your organization’s goals, schedule, and readiness needs. Each package is designed to move teams from awareness to practical response capability.
              </p>
          </div>
        </Reveal>

        <StaggerGroup className="grid-3" stagger={0.08} style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 22, alignItems: "stretch" }}>
          {PACKAGE_DATA.map((program) => (
            <motion.div key={program.title} variants={staggerItem} style={{ height: "100%" }}>
              <PackageCard program={program} />
            </motion.div>
          ))}
        </StaggerGroup>

        <Reveal y={18}>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 44 }}>
            <Btn
              className="mobile-full-button"
              onClick={() => setCompareOpen(true)}
              variant="primary"
              style={{
                padding: "18px 38px",
                fontFamily: "'Barlow', sans-serif",
                fontSize: 16,
                fontWeight: 900,
                letterSpacing: "0.11em",
                boxShadow: `0 14px 40px ${C.red}45`,
              }}
            >
              Compare Packages <ArrowRight size={16} />
            </Btn>
          </div>
        </Reveal>
      </div>
    </section>

    <Reveal y={30}>
      <section style={{ background: C.red, padding: "76px 24px", position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(120deg, rgba(0,0,0,0.18), transparent 45%, rgba(255,255,255,0.08))",
          pointerEvents: "none",
        }} />
        <div style={{ maxWidth: 980, margin: "0 auto", textAlign: "center", position: "relative" }}>
          <h2 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: "clamp(44px, 6vw, 76px)",
            fontWeight: 900,
            fontStyle: "italic",
            color: C.white,
            textTransform: "uppercase",
            letterSpacing: 0,
            lineHeight: 0.95,
            margin: "0 0 34px",
          }}>
            Build a safer, more prepared organization.
          </h2>
          <Btn className="mobile-full-button" onClick={openModal} variant="secondary" style={{
            padding: "18px 44px",
            fontFamily: "'Barlow', sans-serif",
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: "0.1em",
          }}>
            Schedule a Consultation
          </Btn>
        </div>
      </section>
    </Reveal>
    <PackageComparisonModal isOpen={compareOpen} onClose={() => setCompareOpen(false)} />
  </div>
  );
};

const PackageCard = ({ program }) => {
  const [hov, setHov] = useState(false);
  const Icon = program.icon;
  const accent = C.redLight;

  return (
    <motion.div
      className="card-pad"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25, ease: EASE }}
      style={{
        height: "100%",
        minHeight: 560,
        display: "flex",
        flexDirection: "column",
        background: hov ? "linear-gradient(180deg, #232327 0%, #17171a 100%)" : "linear-gradient(180deg, #1b1b1f 0%, #141417 100%)",
        border: `1px solid ${hov ? accent : C.zinc800}`,
        padding: 38,
        boxShadow: hov ? `0 24px 58px rgba(0,0,0,0.48), 0 0 34px ${accent}30` : "0 12px 32px rgba(0,0,0,0.18)",
        transition: "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 5, background: accent }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 30 }}>
        <div style={{
          width: 62,
          height: 62,
          display: "grid",
          placeItems: "center",
          background: `${accent}16`,
          border: `1px solid ${accent}55`,
          color: accent,
        }}>
          <Icon size={30} />
        </div>
      </div>

      <h3 className="package-title" style={{
        ...midHead,
        fontSize: "clamp(32px, 3vw, 44px)",
        minHeight: "clamp(88px, 7vw, 104px)",
        display: "flex",
        alignItems: "flex-end",
        marginBottom: 24,
      }}>{program.title}</h3>

      <div className="package-meta-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
        <div style={{ background: "rgba(255,255,255,0.035)", border: `1px solid ${C.zinc800}`, padding: "14px 15px" }}>
          <p style={programLabelStyle}>Time</p>
          <p style={{ ...programBodyStyle, fontSize: 15, lineHeight: 1.45, margin: 0 }}>{program.duration}</p>
        </div>
        <div style={{ background: "rgba(255,255,255,0.035)", border: `1px solid ${C.zinc800}`, padding: "14px 15px" }}>
          <p style={programLabelStyle}>Format</p>
          <p style={{ ...programBodyStyle, fontSize: 15, lineHeight: 1.45, margin: 0 }}>{program.format}</p>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <p style={programLabelStyle}>Ideal For</p>
        <p className="readable-copy" style={{ ...programBodyStyle, margin: 0 }}>{program.idealFor}</p>
      </div>

      <div style={{ borderTop: `1px solid ${C.zinc800}` }}>
        <PackageAccordion title="Program Focus">
          <p className="readable-copy" style={{ ...programBodyStyle, color: C.zinc300, margin: 0 }}>{program.overview}</p>
        </PackageAccordion>
        <PackageAccordion title="Includes">
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {program.includes.slice(0, 6).map((item) => (
              <li key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <CheckCircle size={18} color={accent} style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: C.white,
                  lineHeight: 1.45,
                }}>
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </PackageAccordion>
        <PackageAccordion title="Outcome">
          <p className="readable-copy" style={{ ...programBodyStyle, color: C.white, margin: 0 }}>{program.outcome}</p>
        </PackageAccordion>
      </div>
    </motion.div>
  );
};

const PackageAccordion = ({ title, children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderBottom: `1px solid ${C.zinc800}` }}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "18px 0",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: C.white,
          textAlign: "left",
        }}
      >
        <span style={{
          fontFamily: "'Barlow', sans-serif",
          fontSize: 14,
          fontWeight: 900,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: open ? C.redLight : C.white,
        }}>
          {title}
        </span>
        <span style={{
          width: 30,
          height: 30,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          background: open ? `${C.red}22` : "rgba(255,255,255,0.04)",
          border: `1px solid ${open ? C.redLight : C.zinc700}`,
          color: open ? C.redLight : C.white,
          transition: "background 0.2s ease, border-color 0.2s ease, color 0.2s ease",
        }}>
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: EASE }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ padding: "0 0 20px" }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PackageComparisonTable = () => (
  <Reveal>
    <div className="compare-table-wrap" style={{
      overflowX: "auto",
      border: `1px solid ${C.zinc800}`,
      background: "linear-gradient(180deg, rgba(24,24,27,0.92), rgba(12,12,14,0.96))",
      boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
    }}>
      <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Package", "Time", "Format", "Main Difference"].map((head) => (
              <th key={head} style={{
                padding: "22px 24px",
                background: C.black,
                borderBottom: `1px solid ${C.zinc800}`,
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: 14,
                fontWeight: 900,
                color: C.redLight,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                textAlign: "left",
              }}>
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PACKAGE_DATA.map((program) => (
            <tr key={program.title}>
              <td style={packageTableCellStyle}>
                <strong style={{ color: C.white }}>{program.title}</strong>
              </td>
              <td style={packageTableCellStyle}>{program.duration}</td>
              <td style={packageTableCellStyle}>{program.format}</td>
              <td style={packageTableCellStyle}>{program.outcome}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Reveal>
);

const PackageComparisonModal = ({ isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2100,
          background: "rgba(0,0,0,0.92)",
          backdropFilter: "blur(14px)",
          padding: "22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          className="modal-panel"
          initial={{ opacity: 0, y: 18, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 18, scale: 0.97 }}
          transition={{ duration: 0.24, ease: EASE }}
          style={{
            width: "min(1180px, 100%)",
            maxHeight: "calc(100vh - 44px)",
            overflowY: "auto",
            background: "linear-gradient(180deg, #111114 0%, #080809 100%)",
            border: `1px solid ${C.zinc800}`,
            boxShadow: "0 32px 90px rgba(0,0,0,0.72)",
            position: "relative",
            padding: "44px",
          }}
        >
          <button
            onClick={onClose}
            aria-label="Close comparison"
            style={{
              position: "absolute",
              top: 18,
              right: 18,
              width: 44,
              height: 44,
              display: "grid",
              placeItems: "center",
              background: C.zinc900,
              border: `1px solid ${C.zinc700}`,
              color: C.white,
              cursor: "pointer",
            }}
          >
            <X size={22} />
          </button>
          <div style={{ maxWidth: 850, marginBottom: 32, paddingRight: 48 }}>
            <Tag>Side-By-Side Comparison</Tag>
            <h2 style={{ ...sectionHead, marginBottom: 18 }}>Compare Packages</h2>
            <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 19, color: C.zinc300, lineHeight: 1.8, margin: 0 }}>
              A simple overview of the time, format, and primary difference for each preparedness option.
            </p>
          </div>
          <PackageComparisonTable />
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const ExpertisePage = ({ navigate, openModal }) => (
  <div style={{ paddingTop: 116, background: C.zinc950, minHeight: "100vh" }}>
    <PageHero
      navigate={navigate}
      showMobileBack
      tag="Our Philosophy"
      title={<>STRATEGIC<br />THREAT<br /><span style={{ color: C.zinc500 }}>MINDSET.</span></>}
      subtitle="From individual de-escalation skills to enterprise-wide security architecture, we deliver the expertise required to mitigate modern threats."
    />

    <section className="section-pad" style={{ padding: "80px 24px", borderBottom: `1px solid ${C.zinc900}` }}>
      <StaggerGroup className="grid-2" stagger={0.12} style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px 48px" }}>
        {EXPERTISE_SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <motion.div key={s.title} variants={staggerItem} style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
              <div style={{ padding: 20, background: C.zinc900, border: `1px solid ${C.zinc800}`, flexShrink: 0 }}>
                <Icon size={36} color={C.red} />
              </div>
              <div>
                <h3 style={midHead}>{s.title}</h3>
                <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, color: C.zinc300, lineHeight: 1.75, marginBottom: 20 }}>{s.description}</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {s.points.map((pt) => (
                    <div key={pt} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.red, flexShrink: 0 }} />
                      <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 14, fontWeight: 800, color: C.zinc300, letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: 1.4 }}>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          );
        })}
      </StaggerGroup>
    </section>

    <section className="section-pad" style={{ padding: "80px 24px" }}>
      <div className="grid-main" style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "5fr 7fr", gap: 64 }}>
        <Reveal>
          <div style={{ position: "sticky", top: 100, alignSelf: "start" }}>
            <Tag>The Competitive Edge</Tag>
            <h3 style={{ ...sectionHead, marginTop: 12 }}>What Sets Us <span style={{ color: C.zinc500 }}>Apart.</span></h3>
            <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, color: C.zinc300, lineHeight: 1.8, marginBottom: 16 }}>
              Modern Threat Consulting LLC is built on real-world experience.
            </p>
            <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, color: C.zinc300, lineHeight: 1.8 }}>
              Our instructors bring nearly two decades of federal law enforcement experience, operating in high-threat, high-stress environments. This allows us to deliver training that goes far beyond standard presentations.
            </p>
          </div>
        </Reveal>
        <StaggerGroup className="grid-2" stagger={0.08} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {DIFFERENTIATORS.map((d) => (
            <motion.div key={d.title} variants={staggerItem}>
              <InfoCard icon={Crosshair} title={d.title} desc={d.text} />
            </motion.div>
          ))}
        </StaggerGroup>
      </div>
    </section>

    <CtaBand onContact={openModal} />
  </div>
);

const CategoryPage = ({ data, navigate, openModal }) => {
  const Icon = data.icon;

  return (
    <div style={{ paddingTop: 116, background: C.zinc950, minHeight: "100vh" }}>
      <section style={{ background: C.black, padding: "80px 24px 72px", borderBottom: `1px solid ${C.zinc900}` }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Reveal>
            <button onClick={() => navigate("home")} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 900, color: C.zinc300, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 48, padding: 0 }}>
              <ArrowRight size={14} style={{ transform: "rotate(180deg)" }} /> Back to Home
            </button>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <Icon size={28} color={C.red} />
              <Tag>Service Industry</Tag>
            </div>
          </Reveal>
          <Reveal delay={0.2} y={40}>
            <h1 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontStyle: "italic", fontSize: "clamp(56px, 8vw, 104px)", color: C.white, textTransform: "uppercase", letterSpacing: 0, lineHeight: 0.88, margin: "0 0 24px", maxWidth: 900 }}>
              {data.title}
            </h1>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 21, color: C.zinc300, fontWeight: 400, lineHeight: 1.7, maxWidth: 700 }}>{data.subheadline}</p>
          </Reveal>
        </div>
      </section>

      {data.id === "realestate" && (
        <section className="section-pad" style={{ background: C.zinc950, borderBottom: `1px solid ${C.zinc900}`, padding: "80px 24px" }}>
          <div className="grid-2" style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
            <Reveal>
              <Tag>Our Commitment</Tag>
              <h2 style={{ ...sectionHead, marginTop: 16 }}>Real Estate Agent Safety Starts With Confidence</h2>
              <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 19, color: C.zinc300, fontWeight: 400, lineHeight: 1.8 }}>
                Feel prepared, in control, and protected—every showing, every open house, every client interaction.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div style={{ borderLeft: `3px solid ${C.red}`, paddingLeft: 28 }}>
                <h3 style={midHead}>You Should Never Have to Choose Between Success and Safety</h3>
                <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, color: C.zinc300, lineHeight: 1.8, margin: 0 }}>
                  Meeting new clients alone. Walking into vacant homes. Hosting open houses with strangers. These moments are part of your career, but feeling vulnerable does not have to be.
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="section-pad" style={{ padding: "80px 24px" }}>
        <div className="grid-2" style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "start" }}>
          <Reveal>
            <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
              <div>
                <Tag>The Challenge</Tag>
                <h3 style={midHead}>{data.problemHeadline}</h3>
                <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, color: C.zinc300, lineHeight: 1.8 }}>{data.problem}</p>
              </div>
              <div style={{ background: C.zinc900, border: `1px solid ${C.zinc800}`, padding: 36 }}>
                <p style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 900, color: C.zinc300, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 24 }}>Industry Professionals</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {data.who.map((item) => (
                    <div key={item} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Shield size={13} color={C.red} style={{ flexShrink: 0 }} />
                      <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, color: C.white, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.35 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} y={40}>
            <div style={{ background: C.white, borderTop: `6px solid ${C.red}`, padding: 48, boxShadow: "0 32px 80px rgba(0,0,0,0.5)" }}>
              <p style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 900, color: C.red, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 18 }}>Industry-Specific Topics</p>
              <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 17, fontWeight: 500, color: C.zinc800, lineHeight: 1.65, margin: "0 0 38px" }}>
                We build upon your existing training by adapting preparedness concepts to your people, environment, and real-world threat profile.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 28 }}>
                {data.solution.map((item) => (
                  <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                    <CheckCircle size={22} color={C.red} style={{ flexShrink: 0 }} />
                    <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "clamp(16px,2vw,22px)", fontWeight: 900, fontStyle: "italic", color: C.black, textTransform: "uppercase", letterSpacing: 0, lineHeight: 1.1 }}>{item}</span>
                  </li>
                ))}
              </ul>
              <Btn className="mobile-full-button" onClick={openModal} variant="primary" style={{ width: "100%", marginTop: 48, padding: 16 }}>
                Request Industry Proposal
              </Btn>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand onContact={openModal} />
    </div>
  );
};

const FeatureList = ({ points }) => (
  <div className="card-pad" style={{ background: `${C.zinc900}80`, border: `1px solid ${C.zinc800}`, padding: "40px 48px" }}>
    <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 14, fontWeight: 800, color: C.zinc300, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 32 }}>Included In Service</p>
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 20 }}>
      {points.map((pt, pi) => (
        <motion.li
          key={pt}
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: pi * 0.06, ease: EASE }}
          style={{ display: "flex", alignItems: "flex-start", gap: 14 }}
        >
          <CheckCircle size={19} color={C.redLight} style={{ marginTop: 2, flexShrink: 0 }} />
          <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 16, fontWeight: 800, color: C.white, letterSpacing: "0.08em", textTransform: "uppercase", lineHeight: 1.55 }}>{pt}</span>
        </motion.li>
      ))}
    </ul>
  </div>
);

const PageHero = ({ tag, title, subtitle, navigate, showMobileBack = false }) => {
  const reduce = useReducedMotion();

  return (
    <section style={{ background: C.black, padding: "100px 24px 80px", borderBottom: `1px solid ${C.zinc900}`, position: "relative", overflow: "hidden" }}>
      <motion.div
        animate={reduce ? {} : { opacity: [0.4, 0.7, 0.4] }}
        transition={reduce ? {} : { duration: 6, repeat: Infinity, ease: "easeInOut" }}
        style={{ position: "absolute", right: 0, top: 0, width: "30%", height: "100%", background: `${C.red}08`, filter: "blur(80px)", pointerEvents: "none" }}
      />
      <div style={{ maxWidth: 1280, margin: "0 auto", position: "relative" }}>
        {showMobileBack && (
          <Reveal>
            <button
              className="mobile-back-home"
              onClick={() => navigate?.("home")}
              style={{
                alignItems: "center",
                gap: 10,
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "'Barlow', sans-serif",
                fontSize: 15,
                fontWeight: 900,
                color: C.zinc300,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                marginBottom: 48,
                padding: 0,
              }}
            >
              <ArrowRight size={18} style={{ transform: "rotate(180deg)" }} /> Back to Home
            </button>
          </Reveal>
        )}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: EASE }}>
          <Tag>{tag}</Tag>
        </motion.div>
        <motion.h1
          className="page-hero-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontStyle: "italic", fontSize: "clamp(64px, 10vw, 120px)", color: C.white, textTransform: "uppercase", letterSpacing: 0, lineHeight: 0.88, margin: "16px 0 28px" }}
        >
          {title}
        </motion.h1>
        <motion.p className="readable-copy" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4, ease: EASE }} style={{ fontFamily: "'Barlow', sans-serif", fontSize: "clamp(18px,1.8vw,22px)", color: C.zinc300, fontWeight: 400, lineHeight: 1.75, maxWidth: 700 }}>
          {subtitle}
        </motion.p>
      </div>
    </section>
  );
};

const CtaBand = ({ onContact }) => {
  const reduce = useReducedMotion();

  return (
    <Reveal y={30}>
      <div style={{ background: C.red, padding: "72px 24px", position: "relative", overflow: "hidden" }}>
        {!reduce && (
          <motion.div
            animate={{ x: ["-10%", "110%"] }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            style={{ position: "absolute", top: 0, bottom: 0, width: "20%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)", pointerEvents: "none" }}
          />
        )}
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center", position: "relative" }}>
          <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontStyle: "italic", fontSize: "clamp(44px, 6vw, 72px)", color: C.white, textTransform: "uppercase", letterSpacing: 0, marginBottom: 20 }}>
            Ready to Modernize Your Training?
          </h2>
          <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.92)", fontSize: 19, fontWeight: 400, marginBottom: 36, lineHeight: 1.65 }}>
            Our subject matter experts deliver elite training tailored to your specific environment and personnel.
          </p>
          <Btn className="mobile-full-button" onClick={onContact} variant="secondary" style={{
            padding: "18px 44px",
            fontFamily: "'Barlow', sans-serif",
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: "0.12em",
          }}>
            Request Custom Training Proposal
          </Btn>
        </div>
      </div>
    </Reveal>
  );
};

const ContactModal = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const formData = new FormData(e.target);
    formData.append("access_key", "12ea8ff4-a106-469d-972d-ae065d8ea42f");
    formData.append("subject", "New Training Inquiry - Modern Threat Consulting");
    formData.append("from_name", "Modern Threat Consulting Portal");
    formData.append("to", "info@modernthreattraining.com");

    try {
      const res = await fetch("https://api.web3forms.com/submit", { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) {
        setStatus("success");
        setTimeout(() => {
          onClose();
          setStatus("idle");
        }, 3000);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)" }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            className="modal-panel"
            initial={{ scale: 0.96, y: 8, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 8, opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            style={{ background: C.zinc950, border: `1px solid ${C.zinc800}`, padding: 48, width: "100%", maxWidth: 520, position: "relative" }}
          >
            <button onClick={onClose} style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", cursor: "pointer", color: C.zinc500 }}>
              <X size={22} />
            </button>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: C.red }} />
            <Tag>Direct Channel</Tag>
            <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 40, fontWeight: 900, fontStyle: "italic", color: C.white, textTransform: "uppercase", letterSpacing: 0, margin: "8px 0 28px" }}>Get In Touch</h2>

            {status === "success" ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: "center", padding: "40px 0" }}>
                <CheckCircle size={56} color="#22c55e" style={{ margin: "0 auto 20px" }} />
                <p style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 18, color: C.white, letterSpacing: "0.2em", textTransform: "uppercase" }}>Inquiry Received</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <input name="name" placeholder="NAME" required style={inputStyle} />
                  <input type="email" name="email" placeholder="EMAIL" required style={inputStyle} />
                </div>
                <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <input name="organization" placeholder="ORGANIZATION" required style={inputStyle} />
                  <input name="phone" type="tel" placeholder="PHONE" style={inputStyle} />
                </div>
                <textarea name="message" rows={4} placeholder="MESSAGE" style={{ ...inputStyle, resize: "vertical" }} />
                <Btn type="submit" disabled={status === "sending"} style={{ width: "100%", marginTop: 8 }}>
                  {status === "sending" ? "Transmitting..." : "Send Inquiry"} {status !== "sending" && <ArrowRight size={14} />}
                </Btn>
                {status === "error" && <p style={{ color: "#ef4444", fontFamily: "'Barlow Condensed', sans-serif", fontSize: 11, letterSpacing: "0.15em", textAlign: "center" }}>Submission failed. Please try again.</p>}
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Footer = ({ navigate }) => (
  <footer style={{ background: C.black, borderTop: `1px solid ${C.zinc900}`, padding: "80px 24px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <Reveal>
        <div className="footer-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 48, marginBottom: 60 }}>
          <div>
            <div style={{ marginBottom: 24 }}><Logo /></div>
            <p className="readable-copy" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 16, color: C.zinc300, lineHeight: 1.8, maxWidth: 420, letterSpacing: "0.02em", textTransform: "uppercase" }}>
              Comprehensive, reality-based training designed to prepare organizations for the unthinkable.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
              {SOCIAL_LINKS.map((social) => (
                <SocialLink key={social.label} social={social} />
              ))}
            </div>
          </div>
          <FooterColumn title="Expertise" items={EXPERTISE_SECTIONS.map((item) => item.title)} onClick={() => navigate("expertise")} />
          <FooterColumn title="Programs" items={["Preparedness Programs", "Compare Packages"]} onClick={() => navigate("programs")} />
          <FooterColumn title="Industries" items={Object.values(CATEGORY_DATA).map((cat) => cat.title)} onClick={(label) => navigate(Object.values(CATEGORY_DATA).find((cat) => cat.title === label)?.id || "home")} />
        </div>
      </Reveal>
      <Divider />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, paddingTop: 28 }}>
        <p style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, color: C.zinc400, letterSpacing: "0.12em", textTransform: "uppercase" }}>
          (c) {new Date().getFullYear()} Modern Threat Consulting LLC. All Rights Reserved.
        </p>
        <div style={{ display: "flex", gap: 24 }}>
          {["Privacy Policy", "Terms of Service"].map((t) => (
            <span key={t} style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, color: C.zinc400, letterSpacing: "0.12em", textTransform: "uppercase" }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

const SocialLink = ({ social }) => {
  const [hov, setHov] = useState(false);
  const Icon = social.icon;

  return (
    <a
      href={social.href}
      target="_blank"
      rel="noreferrer"
      aria-label={social.label}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 46,
        height: 46,
        display: "inline-grid",
        placeItems: "center",
        background: hov ? C.red : C.zinc900,
        border: `1px solid ${hov ? C.redLight : C.zinc800}`,
        color: C.white,
        textDecoration: "none",
        boxShadow: hov ? `0 12px 28px ${C.red}35` : "none",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        transition: "background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
      }}
    >
      <Icon size={21} strokeWidth={2.4} />
    </a>
  );
};

const FooterColumn = ({ title, items, onClick }) => (
  <div>
    <h4 style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, fontWeight: 800, color: C.white, letterSpacing: "0.12em", textTransform: "uppercase", margin: "0 0 22px 0" }}>{title}</h4>
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 16 }}>
      {items.map((item) => (
        <li key={item}>
          <FooterColumnItem item={item} onClick={onClick} />
        </li>
      ))}
    </ul>
  </div>
);

const FooterColumnItem = ({ item, onClick }) => {
  const [hov, setHov] = useState(false);

  return (
    <button
      onClick={() => onClick(item)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
        fontFamily: "'Barlow', sans-serif",
        fontSize: 15,
        fontWeight: 800,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: hov ? C.redLight : C.zinc300,
        textAlign: "left",
        lineHeight: 1.35,
        transition: "color 0.16s ease, text-shadow 0.16s ease, filter 0.16s ease",
        textShadow: hov
          ? "0 0 14px rgba(255,26,20,0.45)"
          : "none",
        filter: hov ? "brightness(1.12)" : "none",
      }}
    >
      {item}
    </button>
  );
};

const inputStyle = {
  background: C.zinc900,
  border: `1px solid ${C.zinc800}`,
  padding: "16px 18px",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontSize: 15,
  fontWeight: 700,
  color: C.white,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const sectionHead = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 900,
  fontStyle: "italic",
  fontSize: "clamp(44px, 5.8vw, 72px)",
  color: C.white,
  textTransform: "uppercase",
  letterSpacing: 0,
  lineHeight: 0.92,
  margin: "12px 0 24px",
};

const midHead = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontSize: "clamp(32px,3.8vw,48px)",
  fontWeight: 900,
  fontStyle: "italic",
  color: C.white,
  textTransform: "uppercase",
  letterSpacing: 0,
  margin: "0 0 16px",
};

const smallHead = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontSize: 19,
  fontWeight: 900,
  color: C.white,
  letterSpacing: "0.08em",
  lineHeight: 1.2,
  textTransform: "uppercase",
  marginBottom: 10,
};

const smallBody = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: 17,
  color: C.zinc300,
  lineHeight: 1.7,
};

const programLabelStyle = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: 13,
  fontWeight: 900,
  color: C.redLight,
  letterSpacing: "0.13em",
  textTransform: "uppercase",
  margin: "0 0 10px",
};

const programBodyStyle = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: 17,
  color: C.zinc300,
  lineHeight: 1.72,
};

const packageTableCellStyle = {
  padding: "22px 24px",
  borderBottom: `1px solid ${C.zinc800}`,
  fontFamily: "'Barlow', sans-serif",
  fontSize: 16,
  color: C.zinc300,
  lineHeight: 1.55,
  verticalAlign: "top",
};

export default function App() {
  const [page, setPage] = useState("home");
  const [modalOpen, setModalOpen] = useState(false);

  const navigate = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div style={{ minHeight: "100vh", background: C.black, color: C.zinc400, fontFamily: "'Barlow', sans-serif" }}>
      <Header currentPage={page} navigate={navigate} openModal={() => setModalOpen(true)} />
      <main>
        <AnimatePresence mode="wait">
          <motion.div
            key={page}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
          >
            {page === "home" && <HomePage navigate={navigate} openModal={() => setModalOpen(true)} />}
            {page === "expertise" && <ExpertisePage navigate={navigate} openModal={() => setModalOpen(true)} />}
            {page === "capabilities" && <CapabilitiesPage openModal={() => setModalOpen(true)} />}
            {page === "programs" && <PreparednessProgramsPage navigate={navigate} openModal={() => setModalOpen(true)} />}
            {CATEGORY_DATA[page] && <CategoryPage data={CATEGORY_DATA[page]} navigate={navigate} openModal={() => setModalOpen(true)} />}
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer navigate={navigate} />
      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
